export const MAX_DAILY_DRIVING_HOURS = 9;
export const BREAK_AFTER_HOURS = 4.5;
export const BREAK_DURATION_MINUTES = 45;
const SECONDS_PER_HOUR = 3600;
const HOURS_PER_DAY = 24;

/**
 * ETA breakdown for ware distance calculator and truck toolbar.
 *
 * 1. Driving time = distance / average speed.
 * 2. Trip is split into max 9 h driving days (EU daily limit).
 * 3. Each 9 h day includes one 45 min break (two 4.5 h driving blocks).
 * 4. If the last partial day exceeds 4.5 h driving, add one more 45 min break.
 * 5. Statutory breaks are part of on-road time.
 * 6. Between driving days, add obligatory stationary rest for the remainder of
 *    each 24 h day: 24 h − (9 h driving + 45 min break) = 14 h 15 min per rest period.
 *
 * @param {{ distanceKm?: number|null, avgSpeedKmh?: number|null }} params
 * @returns {{
 *   drivingSeconds: number,
 *   breakSeconds: number,
 *   breakCount: number,
 *   dailyRestSeconds: number,
 *   totalRestSeconds: number,
 *   totalOnRoadSeconds: number,
 *   totalTripSeconds: number,
 * }|null}
 */
export function computeEtaBreakdown({ distanceKm, avgSpeedKmh }) {
    const distance = Number(distanceKm);
    const speed = Number(avgSpeedKmh);

    if (!Number.isFinite(distance) || distance <= 0) return null;
    if (!Number.isFinite(speed) || speed <= 0) return null;

    const drivingHours = distance / speed;
    const fullNineHourDays = Math.floor(drivingHours / MAX_DAILY_DRIVING_HOURS);
    const remainderHours = drivingHours - fullNineHourDays * MAX_DAILY_DRIVING_HOURS;

    let breakCount = fullNineHourDays;
    if (remainderHours > BREAK_AFTER_HOURS) {
        breakCount += 1;
    }

    const breakSeconds = breakCount * BREAK_DURATION_MINUTES * 60;
    const drivingSeconds = Math.round(drivingHours * SECONDS_PER_HOUR);
    const totalOnRoadSeconds = drivingSeconds + breakSeconds;

    const onRoadPerFullDaySeconds =
        MAX_DAILY_DRIVING_HOURS * SECONDS_PER_HOUR + BREAK_DURATION_MINUTES * 60;
    const dailyRestPerPeriodSeconds =
        HOURS_PER_DAY * SECONDS_PER_HOUR - onRoadPerFullDaySeconds;

    let dailyRestPeriodCount = 0;
    if (fullNineHourDays > 0) {
        if (remainderHours > 0) {
            dailyRestPeriodCount = fullNineHourDays;
        } else if (fullNineHourDays > 1) {
            dailyRestPeriodCount = fullNineHourDays - 1;
        }
    }

    const dailyRestSeconds = dailyRestPeriodCount * dailyRestPerPeriodSeconds;
    const totalRestSeconds = breakSeconds + dailyRestSeconds;
    const totalTripSeconds = drivingSeconds + totalRestSeconds;

    return {
        drivingSeconds,
        breakSeconds,
        breakCount,
        dailyRestSeconds,
        totalRestSeconds,
        totalOnRoadSeconds,
        totalTripSeconds,
    };
}

/**
 * Compact duration label; zero/empty returns "0h 0m".
 * @param {number|null|undefined} durationSeconds
 * @param {(seconds: number) => string|null} formatCompact
 * @returns {string}
 */
export function formatDurationCompactAllowZero(durationSeconds, formatCompact) {
    if (!durationSeconds || durationSeconds <= 0) return "0h 0m";
    return formatCompact(durationSeconds) ?? "0h 0m";
}
